import { ensureNoUndefinedProperties } from '@/Shared/Helper/EnsureNoUndefinedProperties'
import GroupSchedulingSettings from '@/App/Service/Entity/GroupSchedulingSettings'
import ServiceID from '@/App/Service/Entity/ServiceID'
import type { TextTokenEnum } from '@/Shared/AutoGenerated/LocalizationEnum'
import {
    SchedulingStrategyEnum,
    MeetingLocationTypeEnum,
    SchedulingStrategyEnumData,
    MeetingLocationPerSchedulingStrategyList,
    MeetingLocationTypeEnumData,
    type ServiceData,
} from '@/Shared/AutoGenerated/Main'
import RecurrenceSettings from '@/App/Service/Entity/RecurrenceSettings'

export default class Service {
    constructor(
        public readonly id: ServiceID,
        public readonly slug: string,
        public readonly schedulingStrategy: SchedulingStrategyEnum,
        public readonly title: string,
        public readonly description: string | null,
        public readonly durationInMinutes: number,
        public readonly intervalBetweenMeetings: number,
        public readonly maximumNumberOfSeatsPerMeeting: number,
        public readonly maximumBookingDaysInAdvance: number,
        public readonly minimumBookingHoursInAdvance: number,
        public readonly minimumCancelationHoursInAdvance: number,
        public readonly isPaymentRequired: boolean,
        public readonly isVisible: boolean,
        public readonly groupSchedulingSettings: GroupSchedulingSettings,
        public readonly recurrenceSettings: RecurrenceSettings,
        public readonly isEnabled: boolean,
    ) {
        ensureNoUndefinedProperties(this)
    }

    static getSchedulingStrategyLabel(
        schedulingStrategy: SchedulingStrategyEnum,
    ): string {
        return SchedulingStrategyEnumData[schedulingStrategy].label
    }

    static getSchedulingStrategyDescription(
        schedulingStrategy: SchedulingStrategyEnum,
    ): string {
        return SchedulingStrategyEnumData[schedulingStrategy].description
    }

    doesNotAllowsRecurrence(): boolean {
        return (
            this.schedulingStrategy !==
            SchedulingStrategyEnum.SingleHostSingleAttendee
        )
    }

    isRecurring(): boolean {
        return this.recurrenceSettings.isRecurring()
    }

    // ------------------------
    // Meeting locations
    // ------------------------

    static getLocationLabelByType = (type: MeetingLocationTypeEnum): string => {
        return MeetingLocationTypeEnumData[type].label
    }

    static getLocalizationTokenByType = (
        type: MeetingLocationTypeEnum,
    ): TextTokenEnum => {
        return MeetingLocationTypeEnumData[type].localizationTextToken
    }

    static isMeetingLocationAllowed(
        schedulingStrategy: SchedulingStrategyEnum,
        type: MeetingLocationTypeEnum,
    ): boolean {
        const availableMeetingLocationTypesPerSchedulingStrategy =
            MeetingLocationPerSchedulingStrategyList.find((list) => {
                return list.schedulingStrategy === schedulingStrategy
            })

        if (!availableMeetingLocationTypesPerSchedulingStrategy) {
            throw new Error(
                `Unhandled scheduling strategy ${schedulingStrategy}`,
            )
        }

        return !!availableMeetingLocationTypesPerSchedulingStrategy.availableMeetingLocationTypes.find(
            (meetingLocationType: MeetingLocationTypeEnum) => {
                return type === meetingLocationType
            },
        )
    }

    // ------------------------
    // Group Scheduling
    // ------------------------

    static isMultipleAttendeesPerMeetingSupported(
        schedulingStrategy: SchedulingStrategyEnum,
    ): boolean {
        return (
            schedulingStrategy ===
            SchedulingStrategyEnum.SingleHostMultipleAttendee
        )
    }

    supportsMultipleAttendeesPerMeeting(): boolean {
        return Service.isMultipleAttendeesPerMeetingSupported(
            this.schedulingStrategy,
        )
    }

    getMaximumNumberOfRequestedSeatsPerAttendee(): number {
        if (!this.supportsMultipleAttendeesPerMeeting()) {
            return 1
        }

        if (!this.groupSchedulingSettings.isMultiSeatAllowed) {
            return 1
        }

        return Math.min(
            this.maximumNumberOfSeatsPerMeeting,
            this.groupSchedulingSettings.maximumNumberOfSeatsPerAttendee,
        )
    }

    shouldDisplayRequestedSeatCountSelector(): boolean {
        return this.getMaximumNumberOfRequestedSeatsPerAttendee() > 1
    }

    getRequestedSeatCountOptions(): number[] {
        const options: number[] = []
        const maximumNumberOfRequestedSeatsPerAttendee =
            this.getMaximumNumberOfRequestedSeatsPerAttendee()

        for (
            let option = 1;
            option <= maximumNumberOfRequestedSeatsPerAttendee;
            option++
        ) {
            options.push(option)
        }

        return options
    }

    // ------------------------
    // Card icon helpers
    // ------------------------

    shouldDisplayMultipleAttendeeIcon(): boolean {
        return Service.isMultipleAttendeesPerMeetingSupported(
            this.schedulingStrategy,
        )
    }

    shouldDisplayPaidIcon(): boolean {
        return this.isPaymentRequired
    }

    shouldDisplayDisabledIcon(): boolean {
        return !this.isEnabled
    }

    shouldDisplayInvisibleIcon(): boolean {
        return !this.isEnabled
    }

    shouldDisplayRecurringIcon(): boolean {
        return this.recurrenceSettings.isRecurring()
    }

    shouldDisplayGroupIcon(): boolean {
        return this.maximumNumberOfSeatsPerMeeting > 1
    }

    // ------------------------
    // Hydration
    // ------------------------

    static hydrateSingle(entityData: ServiceData): Service {
        return new Service(
            new ServiceID(entityData.id),
            entityData.slug,
            entityData.schedulingStrategy,
            entityData.title,
            entityData.description,
            entityData.durationInMinutes,
            entityData.intervalBetweenMeetings,
            entityData.maximumNumberOfSeatsPerMeeting,
            entityData.maximumBookingDaysInAdvance,
            entityData.minimumBookingHoursInAdvance,
            entityData.minimumCancelationHoursInAdvance,
            entityData.isPaymentRequired,
            entityData.isVisible,
            GroupSchedulingSettings.hydrateSingle(
                entityData.groupSchedulingSettings,
            ),
            RecurrenceSettings.hydrateSingle(entityData.recurrenceSettings),
            entityData.isEnabled,
        )
    }

    static hydrateMultiple(entitiesData: any[]): Service[] {
        return entitiesData.map((entityData) => this.hydrateSingle(entityData))
    }
}
